import { message } from "antd";
import { makeAutoObservable, runInAction } from "mobx";
import { GlobalApi } from "./GlobalApi";
import type { RecentSpaceEntry, StockSearchBrief, UserInfo, ComplianceLevelCode } from "./GlobalType";
import { isNicknameMissing } from "./userProfileUtils";

class GlobalModel {
  currentUser: UserInfo | null = null;
  isLoggedIn = false;
  /** 避免重复拉配置；与后台 lark-ops 的 init 模式一致，供首屏从 localStorage 恢复 token */
  initialized = false;
  /** 与 backend.ts 中未登录错误码联动，全局登录弹窗 */
  isLoginModalVisible = false;
  /**
   * 从首页进入陈列仓时写入；空间内从「我的关注」等回到 /lobby/:id
   * 非首页进入空间时可能为 null，此时「我的关注」回首页
   */
  lobbyContextSpaceId: string | null = null;

  /**
   * 登录后顶栏用：可访问空间全量，最近访问优先排序（见 GlobalType 说明）
   */
  recentSpaceEntries: RecentSpaceEntry[] = [];

  /**
   * 「与我相关的空间」列表拉取后写入，供顶栏头像下拉「我的订单」在无 URL 空间上下文时选用默认 spaceId
   */
  ordersMenuFallbackSpaceId: string | null = null;

  /**
   * 已登录但昵称为空时强制展示资料补全层（与登录态并存，无取消入口）。
   */
  profileSetupRequired = false;

  /**
   * 用户从顶栏「个人资料」主动打开同套编辑弹层（有取消/点遮罩关闭，与首登强制区分）。
   */
  profileEditorOpen = false;

  /**
   * 股票基础信息全量（登录后拉取一次，下拉组件仅本地过滤）。
   */
  basicStocksCatalog: StockSearchBrief[] = [];
  basicStocksCatalogLoaded = false;

  /** 港美股清单（登录后后台预加载，供 StockLink / 搜索等场景） */
  overseasStocksCatalog: StockSearchBrief[] = [];
  overseasStocksCatalogLoaded = false;

  /** 系统合规等级；默认 finance_share */
  complianceLevelCode: ComplianceLevelCode = "finance_share";
  complianceLevelLoaded = false;

  constructor() {
    makeAutoObservable(this);
  }

  get financeShareEnabled(): boolean {
    return this.complianceLevelCode === "finance_share";
  }

  async loadComplianceLevel() {
    const level = await GlobalApi.getComplianceLevel();
    runInAction(() => {
      this.complianceLevelCode = level;
      this.complianceLevelLoaded = true;
    });
  }

  /**
   * 首屏从 localStorage 的 token 恢复用户态；无后台的通用配置/OSS 初始化（与 ops 区别）。
   * `initialized` 在首行同步置位，避免 StrictMode 下 effect 双次执行时重复请求。
   */
  async init() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    void this.loadComplianceLevel();

    const hadToken = !!localStorage.getItem("token");
    if (hadToken) {
      const user = await GlobalApi.getUserByToken();
      if (user) {
        runInAction(() => {
          this.isLoggedIn = true;
          this.currentUser = user;
          this.syncProfileSetupGate();
        });
        void this.loadRecentSpaces();
        void this.loadBasicStocksCatalog();
        void this.loadOverseasStocksCatalog();
      } else {
        runInAction(() => {
          localStorage.removeItem("token");
          this.isLoggedIn = false;
          this.currentUser = null;
          this.recentSpaceEntries = [];
          this.ordersMenuFallbackSpaceId = null;
          this.profileSetupRequired = false;
          this.profileEditorOpen = false;
          this.basicStocksCatalog = [];
          this.basicStocksCatalogLoaded = false;
          this.overseasStocksCatalog = [];
          this.overseasStocksCatalogLoaded = false;
        });
        message.warning("登录已过期，请重新登录");
      }
    }
  }

  openLoginModal() {
    this.isLoginModalVisible = true;
  }

  closeLoginModal() {
    this.isLoginModalVisible = false;
  }

  setLoggedIn(logged: boolean, user: UserInfo | null) {
    this.isLoggedIn = logged;
    this.currentUser = user;
    if (!logged) {
      localStorage.removeItem("token");
      this.recentSpaceEntries = [];
      this.ordersMenuFallbackSpaceId = null;
      this.profileSetupRequired = false;
      this.profileEditorOpen = false;
      this.basicStocksCatalog = [];
      this.basicStocksCatalogLoaded = false;
      this.overseasStocksCatalog = [];
      this.overseasStocksCatalogLoaded = false;
    } else {
      this.syncProfileSetupGate();
      void this.loadRecentSpaces();
      void this.loadBasicStocksCatalog();
      void this.loadOverseasStocksCatalog();
    }
  }

  syncProfileSetupGate() {
    this.profileSetupRequired = this.isLoggedIn && isNicknameMissing(this.currentUser);
  }

  openProfileEditor() {
    this.profileEditorOpen = true;
  }

  closeProfileEditor() {
    this.profileEditorOpen = false;
  }

  async refreshUserFromServer() {
    const u = await GlobalApi.getUserByToken();
    if (u) {
      runInAction(() => {
        this.isLoggedIn = true;
        this.currentUser = u;
        this.syncProfileSetupGate();
      });
      void this.loadRecentSpaces();
      void this.loadBasicStocksCatalog();
      void this.loadOverseasStocksCatalog();
    } else {
      runInAction(() => {
        localStorage.removeItem("token");
        this.isLoggedIn = false;
        this.currentUser = null;
        this.recentSpaceEntries = [];
        this.ordersMenuFallbackSpaceId = null;
        this.profileSetupRequired = false;
        this.profileEditorOpen = false;
        this.basicStocksCatalog = [];
        this.basicStocksCatalogLoaded = false;
        this.overseasStocksCatalog = [];
        this.overseasStocksCatalogLoaded = false;
      });
    }
  }

  /**
   * 须为 8 位数字空间 ID；无效或 null 则清空，用于顶栏「我的订单」默认空间
   */
  setOrdersMenuFallbackSpaceId(raw: string | null) {
    const t = raw?.trim() ?? "";
    const next = /^\d{8}$/.test(t) ? t : null;
    this.ordersMenuFallbackSpaceId = next;
  }

  async logout() {
    await GlobalApi.logoutApi();
    runInAction(() => {
      this.setLoggedIn(false, null);
    });
  }

  setLobbyContextSpaceId(id: string | null) {
    this.lobbyContextSpaceId = id;
  }

  async loadRecentSpaces() {
    if (!this.isLoggedIn) {
      runInAction(() => {
        this.recentSpaceEntries = [];
        this.ordersMenuFallbackSpaceId = null;
      });
      return;
    }
    const rows = await GlobalApi.getRecentSpaceVisits();
    runInAction(() => {
      this.recentSpaceEntries = rows;
    });
  }

  async loadBasicStocksCatalog() {
    if (!this.isLoggedIn || this.basicStocksCatalogLoaded) {
      return;
    }
    const rows = await GlobalApi.fetchAllBasicStocks();
    runInAction(() => {
      this.basicStocksCatalog = Array.isArray(rows) ? rows : [];
      this.basicStocksCatalogLoaded = true;
    });
  }

  async loadOverseasStocksCatalog() {
    if (!this.isLoggedIn || this.overseasStocksCatalogLoaded) {
      return;
    }
    const rows = await GlobalApi.fetchOverseasBasicStocks();
    runInAction(() => {
      this.overseasStocksCatalog = Array.isArray(rows) ? rows : [];
      this.overseasStocksCatalogLoaded = true;
    });
  }
}

export const globalModel = new GlobalModel();
